import React, { useState } from "react";
import { useField } from "formik";
import { getErrorAndMessagesUseField } from "./util/general";
import AutoComplete from "./util/MaterialFields/AutoComplete";
import { TFunction } from "i18next";
import { clearArrayInfo } from "functions/util";
import DateFnsUtils from "@date-io/date-fns";
import { ptBR, enUS, fr, es } from "date-fns/locale";
import {
    KeyboardTimePicker,
    MuiPickersUtilsProvider
} from "@material-ui/pickers";
import { FormHelperText, Grid, Typography } from "@material-ui/core";
import { useAuthState } from "_ReactContext/AuthContext";

const localeMap: Compleo.IObject = {
    "pt-BR": ptBR,
    "en-US": enUS,
    fr: fr,
    es: es
};

interface IProps {
    metadata: any;
    label: string;
    helperTextDefault: null | string;
    t: TFunction;
    classes: any;
    required: boolean;
    options: Compleo.IObject[];
    language: string;
    // fieldName: string;
}

const DelayAction = (props: IProps) => {
    const {
        metadata,
        label,
        helperTextDefault,
        t,
        classes,
        required,
        options,
        language
    } = props;
    const [field, meta, helpers] = useField(metadata.fieldName);
    const disableTime = ((field.value || {}).delayDetail || {}).disableTime;
    const excludeTimeControl = disableTime === undefined ? true : disableTime;
    const { timeZone } = useAuthState();
    const { error, message } = getErrorAndMessagesUseField(
        meta.error,
        meta.touched,
        helperTextDefault
    );

    const dateDefault = new Date();
    dateDefault.setHours(9);
    dateDefault.setMinutes(0);

    const handleAutocompleteChange = (value: any) => {
        if ((value || {}).disableTime) {
            helpers.setValue({
                delayDetail: value,
                time: null,
                timeZone: currentTimeZone
            });
        } else {
            const currentValue = field.value || {};
            if (currentValue.time === undefined || currentValue.time === null) {
                currentValue.time = dateDefault;
            }
            currentValue.timeZone = currentTimeZone;
            helpers.setValue({ ...currentValue, delayDetail: value });
        }
    };
    const currentTimeZone =
        timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        "America/Sao_Paulo";

    const handleTimeChange = (value: any) => {
        const currentValue = field.value;
        const newTime = value || dateDefault;
        helpers.setValue({
            ...currentValue,
            time: newTime,
            timeZone: currentTimeZone
        });
    };

    React.useEffect(() => {
        const currentValue = (field.value || {}).delayDetail;
        if (currentValue === undefined || currentValue === null) {
            const defaultValue = getDefaultAutoCompleteValue(
                currentValue,
                options
            );
            helpers.setValue({ ...field.value, delayDetail: defaultValue });
        }
    }, []);

    const AutoCompleteValue = (field.value || {}).delayDetail;
    const TimeValue = (field.value || {}).time;

    return (
        <>
            <Grid container>
                <Grid item xs>
                    <AutoComplete
                        options={options}
                        value={AutoCompleteValue}
                        setValue={handleAutocompleteChange}
                        defaultProps={{ ...field }}
                        label={label}
                        helperText={message || ""}
                        name={field.name}
                        labelProperty="label"
                        error={error}
                        required={required}
                        onBlur={field.onBlur}
                    />
                </Grid>
                {!excludeTimeControl ? (
                    <>
                        <Grid item xs={4}>
                            <MuiPickersUtilsProvider
                                locale={localeMap[language]}
                                utils={DateFnsUtils}
                            >
                                <KeyboardTimePicker
                                    style={{ marginLeft: "8px" }}
                                    autoOk
                                    ampm={false}
                                    variant="inline"
                                    label={t(
                                        `${clearArrayInfo(
                                            metadata.fieldName
                                        )}Time`
                                    )}
                                    value={TimeValue}
                                    onChange={handleTimeChange}
                                />
                            </MuiPickersUtilsProvider>
                        </Grid>
                        <Grid container item xs={2} alignItems="center">
                            <Typography
                                style={{ marginLeft: "8px" }}
                                variant="caption"
                            >
                                Timezone: {currentTimeZone}
                            </Typography>
                        </Grid>
                    </>
                ) : null}
            </Grid>
        </>
    );
};

const getDefaultAutoCompleteValue = (
    value: any,
    optionsValue: Compleo.IObject[]
) => {
    if (value === null || value === undefined) {
        const defaultValue = optionsValue.filter(
            (o: any) => o.default === true
        )[0];
        if (defaultValue !== undefined) {
            return defaultValue;
        } else {
            return value;
        }
    } else {
        return value;
    }
};

export default DelayAction;
